import { tokens } from "../styles/tokens";
import { daysBetween } from "./productSearch";
import type { OccupancyCell } from "./OccupancyLayer";

/**
 * "Pintar por Validade" (ADR 354): classify every OCCUPIED cell by how close
 * its WMS expiry date is, on the stepped ramp `tokens.expiry` (expired →
 * far-dated). Stepped buckets, not a continuous gradient — a legend row with
 * a count is something an operator can point at and price; "somewhat orange"
 * is not.
 *
 * The name is deliberate: the mode paints the DATE, not a risk score. Risk
 * (will it expire *given the consumption rate*, ADR 352) is a different claim
 * and waits until rates have enough snapshots to be trusted.
 *
 * Pure module — the scene layers and the LayerPanel legend both consume it,
 * and the tests own the clock via `todayIso`.
 */

/** Upper bound in days-until-expiry per colored bucket; `-1` = already expired
 *  (strictly before today). Order matches `tokens.expiry`. */
export const EXPIRY_BUCKETS = [
  { key: "expired", maxDays: -1 },
  { key: "d30", maxDays: 30 },
  { key: "d90", maxDays: 90 },
  { key: "d180", maxDays: 180 },
  { key: "over", maxDays: Infinity },
] as const;

export type ExpiryBucketKey = (typeof EXPIRY_BUCKETS)[number]["key"];

/** Occupied-but-undated: the WMS snapshot has no expiry for the address.
 *  Grey says "no data", never "safe". */
export const EXPIRY_NO_DATA_COLOR = tokens.ink[500];

/** Stock is physically present — the only cells the Validade story is about. */
export function occupied(cell: Pick<OccupancyCell, "status">): boolean {
  return cell.status === "counted" || cell.status === "unidentified";
}

/** Bucket index into EXPIRY_BUCKETS/tokens.expiry, or null for no date. */
export function expiryBucketIndex(
  expiryDate: string | null | undefined, todayIso: string,
): number | null {
  if (!expiryDate) return null;
  const left = -daysBetween(expiryDate, todayIso); // days until expiry; negative = past
  if (left < 0) return 0;
  return EXPIRY_BUCKETS.findIndex((b) => left <= b.maxDays);
}

/** Ramp color for a date (grey when the address has none). */
export function expiryColorForDate(
  expiryDate: string | null | undefined, todayIso: string,
): string {
  const i = expiryBucketIndex(expiryDate, todayIso);
  return i === null ? EXPIRY_NO_DATA_COLOR : tokens.expiry[i];
}

/** Deck classifier for `deckGroups`: a color for occupied cells, null for the
 *  rest — the layer mutes them so the expiry story alone carries the scene. */
export function expiryDeckPaint(todayIso: string): (cell: OccupancyCell) => string | null {
  return (cell) => (occupied(cell) ? expiryColorForDate(cell.expiryDate, todayIso) : null);
}

export interface PaintCounts {
  /** Occupied cells per bucket, in the mode's bucket order. */
  buckets: number[];
  /** Occupied cells whose address has no date for the active mode. */
  noData: number;
}

/** Legend tallies over the painted run's cells (occupied only). */
export function expiryCounts(cells: OccupancyCell[], todayIso: string): PaintCounts {
  const buckets = EXPIRY_BUCKETS.map(() => 0);
  let noData = 0;
  for (const cell of cells) {
    if (!occupied(cell)) continue;
    const i = expiryBucketIndex(cell.expiryDate, todayIso);
    if (i === null) noData++;
    else buckets[i]++;
  }
  return { buckets, noData };
}

/** yyyy-mm-dd shifted by `days` — UTC-anchored like `daysBetween`, so the
 *  legend's finder seeds can never straddle a timezone's midnight. */
export function shiftIsoDate(todayIso: string, days: number): string {
  return new Date(Date.parse(todayIso) + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Finder seed for a legend row (ADR 354 → ADR 351: click a bucket, the finder
 * isolates it, the simulator prices it). Cumulative on purpose — "everything
 * expiring within 90 days" is the actionable set; the exclusive band is a
 * legend-reading, not a work order. Null for the no-data row.
 */
export function bucketFinderSeed(
  bucket: ExpiryBucketKey, todayIso: string,
): { expiryOp: "lte" | "gte"; expiryDate: string } | null {
  switch (bucket) {
    case "expired": return { expiryOp: "lte", expiryDate: shiftIsoDate(todayIso, -1) };
    case "d30": return { expiryOp: "lte", expiryDate: shiftIsoDate(todayIso, 30) };
    case "d90": return { expiryOp: "lte", expiryDate: shiftIsoDate(todayIso, 90) };
    case "d180": return { expiryOp: "lte", expiryDate: shiftIsoDate(todayIso, 180) };
    case "over": return { expiryOp: "gte", expiryDate: shiftIsoDate(todayIso, 181) };
    default: return null;
  }
}

/* ── "Pintar por Idade" (ADR 361) ─────────────────────────────────────────────
   Same classifier seam, the OTHER date: how long the pallet has sat in its
   position (dwell from the WMS fifo/entry date). The ramp is `tokens.expiry`
   REVERSED — fresh stock green, year-old stock red — so both modes share one
   urgency vocabulary: red is always "act here". */

/** Upper bound in dwell days per bucket, freshest first. Color for bucket i is
 *  `tokens.expiry[AGING_BUCKETS.length - 1 - i]`. */
export const AGING_BUCKETS = [
  { key: "age30", maxDays: 30 },
  { key: "age90", maxDays: 90 },
  { key: "age180", maxDays: 180 },
  { key: "age365", maxDays: 365 },
  { key: "ageOver", maxDays: Infinity },
] as const;

export type AgingBucketKey = (typeof AGING_BUCKETS)[number]["key"];

/** Bucket index into AGING_BUCKETS, or null when the address has no fifo date.
 *  A future-dated entry (clock skew, pre-registered receipt) clamps to fresh. */
export function agingBucketIndex(
  fifoDate: string | null | undefined, todayIso: string,
): number | null {
  if (!fifoDate) return null;
  const dwell = Math.max(0, daysBetween(fifoDate, todayIso));
  return AGING_BUCKETS.findIndex((b) => dwell <= b.maxDays);
}

/** Ramp color for a dwell (grey when the address has no fifo date). */
export function agingColorForFifo(
  fifoDate: string | null | undefined, todayIso: string,
): string {
  const i = agingBucketIndex(fifoDate, todayIso);
  return i === null ? EXPIRY_NO_DATA_COLOR : tokens.expiry[AGING_BUCKETS.length - 1 - i];
}

/** Deck classifier — occupied cells get an age color, the rest mute. */
export function agingDeckPaint(todayIso: string): (cell: OccupancyCell) => string | null {
  return (cell) => (occupied(cell) ? agingColorForFifo(cell.fifoDate, todayIso) : null);
}

/** Legend tallies for the aging mode (occupied only), AGING_BUCKETS order. */
export function agingCounts(cells: OccupancyCell[], todayIso: string): PaintCounts {
  const buckets = AGING_BUCKETS.map(() => 0);
  let noData = 0;
  for (const cell of cells) {
    if (!occupied(cell)) continue;
    const i = agingBucketIndex(cell.fifoDate, todayIso);
    if (i === null) noData++;
    else buckets[i]++;
  }
  return { buckets, noData };
}

/**
 * Finder seed for an aging legend row — cumulative toward URGENT, mirroring
 * bucketFinderSeed: clicking an old band selects "at least this old" (the
 * actionable set), and the freshest band is the remainder ("arrived within
 * 30 days"). Feeds the finder's Permanência filter as typed strings.
 */
export function agingFinderSeed(
  bucket: AgingBucketKey,
): { dwellOp: "gte" | "lte"; dwellDays: string } | null {
  switch (bucket) {
    case "age30": return { dwellOp: "lte", dwellDays: "30" };
    case "age90": return { dwellOp: "gte", dwellDays: "31" };
    case "age180": return { dwellOp: "gte", dwellDays: "91" };
    case "age365": return { dwellOp: "gte", dwellDays: "181" };
    case "ageOver": return { dwellOp: "gte", dwellDays: "366" };
    default: return null;
  }
}
